import { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { loginWithEmail, handleGoogleCallback, validateToken } from '@/lib/api-client';

interface User {
  id: string;
  email: string;
  name?: string;
  role?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => void;
  handleGoogleCallback: (code: string) => Promise<{ user: User; token: string }>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Get token from localStorage (if we're using token-based auth)
const getStoredUser = (): User | null => {
  const userString = localStorage.getItem('user');
  if (userString) {
    try {
      return JSON.parse(userString);
    } catch {
      return null;
    }
  }
  return null;
};

const getStoredToken = (): string | null => {
  return localStorage.getItem('token');
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(getStoredUser());
  const [token, setToken] = useState<string | null>(getStoredToken());
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Define logout function early to use in useEffect
  const logout = () => {
    // Clear user and token from state and localStorage
    setUser(null);
    setToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setIsAuthenticated(false);
  };

  // Validate token on initial load
  useEffect(() => {
    const validateSession = async () => {
      const storedToken = getStoredToken();
      const storedUser = getStoredUser();

      if (storedToken && storedUser) {
        try {
          const isValid = await validateToken(storedToken);

          if (isValid) {
            setUser(storedUser);
            setToken(storedToken);
            setIsAuthenticated(true);
          } else {
            // Token is invalid, clear storage
            logout();
          }
        } catch (error) {
          console.error('Error validating token:', error);
          logout();
        }
      } else {
        // No token or user in storage
        setIsAuthenticated(false);
      }

      setIsLoading(false);
    };

    validateSession();
  }, []);  // Empty dependency array as logout is now defined outside

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await loginWithEmail({ email, password });

      // Check if the response status = 400 
      if (response.status === 400) {
        throw new Error(response.message);
      }

      const { token, email: _email, username, userId, role } = response.data;

      // Set user and token in state and localStorage
      setUser({ id: userId, email: _email, name: username, role });
      setToken(token);
      localStorage.setItem('user', JSON.stringify({ id: userId, email, name: username, role }));
      localStorage.setItem('token', token);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Login error:', error);
      setIsAuthenticated(false);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithGoogle = () => {
    // Redirect to Google auth URL from the backend
    const googleAuthUrl = `${import.meta.env.VITE_API_URL}${import.meta.env.VITE_AUTH_GOOGLE_ENDPOINT}`;
    window.location.href = googleAuthUrl;
  };

  interface GoogleCallbackResponse {
    user: User;
    token: string;
  }

  const processGoogleCallback = async (code: string): Promise<GoogleCallbackResponse> => {
    try {
      const data = await handleGoogleCallback(code);

      // Handle possible response structures
      const token = data.token;
      const user = data.user || {
        id: data.id || data.userId,
        email: data.email,
        name: data.name || data.username,
        role: data.role,
        active: data.active
      };

      // Check if user is active
      if (!user.active) {
        throw new Error("User is inactive or pending to be approved by the admin. Please contact the admin for access.");
      }

      // Set user and token in state and localStorage
      setUser(user);
      setToken(token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('token', token);
      setIsAuthenticated(true);

      return { user, token };
    } catch (error) {
      console.error('Google callback error:', error);
      setIsAuthenticated(false);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isAuthenticated,
        login,
        loginWithGoogle,
        handleGoogleCallback: processGoogleCallback,
        logout,
        isLoading
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 